// src/features/apps/AppPlatformUtils.ts
//
// Pure helpers for the generic platform rows (app_cfg_platform; Fire TV first):
// which row is the platform's, what a customer vs an operator may send, and the
// store-field grammars the API applies. The API stays the authority on writes.

import {
  ANDROID_APPLICATION_ID_PATTERN,
  APPLE_BUNDLE_ID_PATTERN,
  APP_PLATFORM_CONSTRAINTS,
  type AppPlatformPackageKind,
} from '@/lib/constants/appPlatform';
import { APP_STATE_TYPE_ID } from '@/lib/constants/app';
import type {
  AppCfgPlatformCreateReq,
  AppCfgPlatformRow,
  AppCfgPlatformUpdateReq,
  AppCfgsDto,
  AppPlatformFormData,
} from '@/lib/types/App';

export class AppPlatformUtils {
  /**
   * The API carries the platform rows. An API that predates them sends no
   * `platforms` key at all — the platform tabs stay absent then, because their
   * routes do not exist there either.
   */
  static isAvailable(app: AppCfgsDto): boolean {
    return Array.isArray(app.platforms);
  }

  /** The app's row for one platform; null = not enabled yet. */
  static rowFor(app: AppCfgsDto, appPlatformTypeId: number): AppCfgPlatformRow | null {
    return (app.platforms ?? []).find((r) => r.appPlatformTypeId === appPlatformTypeId) ?? null;
  }

  /** Enable = an active row with the launcher label. Never a store field: those are operator-set later. */
  static buildCreateReq(appCfgId: number, title: string): AppCfgPlatformCreateReq {
    return { appCfgId, appStateTypeId: APP_STATE_TYPE_ID.Active, title: title.trim() };
  }

  /** Unset store fields arrive as ABSENT keys; the form holds '' for them. */
  static transformRowToFormData(row: AppCfgPlatformRow): AppPlatformFormData {
    return {
      title: row.title ?? '',
      appStateTypeId: row.appStateTypeId,
      storePackageId: row.storePackageId ?? '',
      storeAppId: row.storeAppId ?? '',
    };
  }

  /** The package cannot change while the row carries a store app id (the store knows the app by it). */
  static isPackageLocked(row: AppCfgPlatformRow): boolean {
    return !!row.storeAppId;
  }

  /**
   * The update body. A CUSTOMER's request never carries the store keys (absent =
   * keep; anything else is a 403). An operator sends a store key only when it
   * changed — '' clears it — and never the package while it is locked.
   */
  static transformFormToUpdateReq(
    form: AppPlatformFormData,
    row: AppCfgPlatformRow,
    isOperator: boolean,
  ): AppCfgPlatformUpdateReq {
    const req: AppCfgPlatformUpdateReq = {
      appStateTypeId: form.appStateTypeId,
      title: form.title.trim(),
    };
    if (!isOperator) return req;
    const pkg = form.storePackageId.trim();
    const appId = form.storeAppId.trim();
    if (!AppPlatformUtils.isPackageLocked(row) && pkg !== (row.storePackageId ?? '')) {
      req.storePackageId = pkg;
    }
    if (appId !== (row.storeAppId ?? '')) req.storeAppId = appId;
    return req;
  }

  /** '' is valid (unset); a value follows the platform family's grammar. */
  static isValidStorePackageId(kind: AppPlatformPackageKind, value: string): boolean {
    const v = value.trim();
    if (v === '') return true;
    if (v.length > APP_PLATFORM_CONSTRAINTS.STORE_PACKAGE_ID_MAX) return false;
    return (kind === 'android' ? ANDROID_APPLICATION_ID_PATTERN : APPLE_BUNDLE_ID_PATTERN).test(v);
  }

  /** '' is valid (unset); a value carries no whitespace or control characters. */
  static isValidStoreAppId(value: string): boolean {
    const v = value.trim();
    if (v === '') return true;
    if (v.length > APP_PLATFORM_CONSTRAINTS.STORE_APP_ID_MAX) return false;
    // eslint-disable-next-line no-control-regex
    return !/[\s\u0000-\u001f\u007f]/.test(v);
  }

  /** The API refuses a store app id on a row without a package. */
  static appIdHasPackage(form: Pick<AppPlatformFormData, 'storePackageId' | 'storeAppId'>): boolean {
    return form.storeAppId.trim() === '' || form.storePackageId.trim() !== '';
  }
}
